<template>
  <div class="w-full rounded-xl overflow-hidden">
    <!-- Hero Image Section -->
    <div class="flex flex-col md:flex-row w-full gap-0">
      <div class="w-full md:grow" ref="mainCarouselRef">
        <Carousel
          ref="mainCarousel"
          class="relative w-full"
          @init-api="(val) => emblaMainApi = val"
        >
          <CarouselContent>
            <CarouselItem v-for="(photo, index) in photos" :key="index">
              <img 
                :src="photo" 
                :alt="`Car photo ${index + 1}`" 
                class="w-full h-auto object-cover" 
                @load="onImageLoad"
              />
            </CarouselItem>
          </CarouselContent>
          
          <!-- Navigation arrows - more visible on mobile -->
          <CarouselPrevious class="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white/95 dark:bg-black/80 dark:hover:bg-black/95 border border-gray-200 dark:border-gray-600 shadow-lg" />
          <CarouselNext class="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white/95 dark:bg-black/80 dark:hover:bg-black/95 border border-gray-200 dark:border-gray-600 shadow-lg" />
          
          <!-- Photo counter for mobile - shows current position -->
          <div v-if="photos.length > 1" class="absolute bottom-3 right-3 md:hidden bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm z-10">
            {{ selectedIndex + 1 }} / {{ photos.length }}
          </div>
          
          <!-- Swipe indicator for mobile (only show on first load briefly) -->
          <div v-if="photos.length > 1 && selectedIndex === 0" class="absolute bottom-3 left-3 md:hidden bg-black/70 text-white px-3 py-1 rounded-full text-xs backdrop-blur-sm animate-pulse z-10">
            ← Swipe →
          </div>
        </Carousel>
      </div>
      
      <!-- Desktop Vertical Thumbnails (hidden on mobile) -->
      <div v-if="photos.length > 1" class="hidden md:block w-1/3">
        <Carousel
          orientation="vertical"
          class="relative w-full"
          :opts="{
            align: 'start',
            containScroll: 'keepSnaps',
          }"
          @init-api="(val) => emblaThumbnailApi = val"
        >
          <CarouselContent :style="{ height: `${mainCarouselHeight + 15}px` }">
            <CarouselItem 
              v-for="(photo, index) in photos" 
              :key="index" 
              class="h-1/3 pt-0 cursor-pointer basis-1/3" 
              @click="onThumbClick(index)"
            >
              <div 
                class="transition-opacity duration-300 ease-in-out h-full bg-cover bg-center border-l-4 border-t-4 border-white dark:border-black"
                :class="index === selectedIndex ? 'opacity-100 ring-2 ring-primary' : 'opacity-98 hover:opacity-75'"
                :style="{ backgroundImage: `url(${photo})` }"
                :aria-label="`Thumbnail ${index + 1}`"
              ></div>
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious class="absolute top-4 -translate-x-1/2 bg-white/70 hover:bg-white/90 dark:bg-black/70" />
          <CarouselNext class="absolute bottom-4 -translate-x-1/2 bg-white/70 hover:bg-white/90 dark:bg-black/70" />
        </Carousel>
      </div>
    </div>

    <!-- Mobile Thumbnail Gallery (shown only on mobile) -->
    <div v-if="photos.length > 1" class="block md:hidden mt-6 px-4">
      <!-- Thumbnail gallery title for clarity -->
      <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
        Gallery ({{ photos.length }} photos)
      </h3>
      
      <div 
        ref="mobileThumbnailContainer"
        class="flex gap-3 overflow-x-auto pb-3 scrollbar-thin scrollbar-track-gray-100 dark:scrollbar-track-gray-700 scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-500"
      >
        <div 
          v-for="(photo, index) in photos" 
          :key="`mobile-${index}`"
          class="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 cursor-pointer transition-all duration-300 relative"
          :class="index === selectedIndex ? 'border-primary ring-2 ring-primary/30 shadow-lg scale-105' : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500 shadow-sm hover:shadow-md'"
          @click="onThumbClick(index)"
        >
          <img 
            :src="photo" 
            :alt="`Thumbnail ${index + 1}`"
            class="w-full h-full object-cover transition-all duration-300"
            :class="index === selectedIndex ? 'brightness-110' : 'hover:brightness-105'"
          />
          
          <!-- Active indicator for selected thumbnail -->
          <div v-if="index === selectedIndex" class="absolute inset-0 bg-primary/20 flex items-center justify-center">
            <div class="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
              <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'

interface Props {
  photos: string[]
}

const props = defineProps<Props>()

const emblaMainApi = ref<CarouselApi | null>(null)
const emblaThumbnailApi = ref<CarouselApi | null>(null)
const selectedIndex = ref(0)
const mainCarouselHeight = ref(0)
const mainCarouselRef = ref<HTMLElement | null>(null)
const mobileThumbnailContainer = ref<HTMLElement | null>(null)

// Define default dimensions for main images (adjust as needed)
const mainImageWidth = 800 // Example width
const mainImageHeight = 600 // Example height

function onSelect() {
  if (!emblaMainApi.value) return
  selectedIndex.value = emblaMainApi.value.selectedScrollSnap()
  syncThumbnailPosition()
  centerMobileThumbnail()
}

function syncThumbnailPosition() {
  if (!emblaThumbnailApi.value) return
  emblaThumbnailApi.value.scrollTo(selectedIndex.value)
}

function centerMobileThumbnail() {
  if (!mobileThumbnailContainer.value) return
  
  nextTick(() => {
    const container = mobileThumbnailContainer.value!
    const thumbnails = container.children
    
    if (thumbnails.length === 0 || selectedIndex.value >= thumbnails.length) return
    
    const activeThumbnail = thumbnails[selectedIndex.value] as HTMLElement
    const containerWidth = container.clientWidth
    const thumbnailWidth = activeThumbnail.offsetWidth
    const thumbnailLeft = activeThumbnail.offsetLeft
    const containerScrollWidth = container.scrollWidth
    
    // Calculate scroll position to center the active thumbnail
    let scrollLeft = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2)
    
    // Calculate constraints for smooth scrolling boundaries
    
    // Ensure we don't scroll past the beginning
    scrollLeft = Math.max(0, scrollLeft)
    
    // Ensure we don't scroll past the end
    const maxScrollLeft = containerScrollWidth - containerWidth
    scrollLeft = Math.min(maxScrollLeft, scrollLeft)
    
    // Only scroll if there's actually a change needed
    if (Math.abs(container.scrollLeft - scrollLeft) > 5) {
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      })
    }
  })
}

function onThumbClick(index: number) {
  if (!emblaMainApi.value) return
  emblaMainApi.value.scrollTo(index)
  // centerMobileThumbnail will be called by the onSelect callback
}

function updateMainCarouselHeight() {
  if (!emblaMainApi.value) return
  const height = emblaMainApi.value.rootNode().offsetHeight
  mainCarouselHeight.value = height
}

function onImageLoad() {
  // Delay to ensure the image has rendered
  setTimeout(() => {
    updateMainCarouselHeight()
    // Also sync thumbnail positions after image loads
    syncThumbnailPosition()
  }, 100)
}

watch(emblaMainApi, (api) => {
  if (api) {
    api.on('select', onSelect)
    api.on('reInit', onSelect)
    api.on('resize', updateMainCarouselHeight)
    // Force initial sync
    nextTick(() => {
      onSelect()
      updateMainCarouselHeight()
    })
  }
})

watch(emblaThumbnailApi, (api) => {
  if (api) {
    // Force initial sync
    nextTick(() => {
      syncThumbnailPosition()
    })
  }
})

// Watch for photos prop changes (important for navigation)
watch(() => props.photos, (newPhotos, oldPhotos) => {
  if (newPhotos !== oldPhotos) {
    // Reset selectedIndex when photos change
    selectedIndex.value = 0
    
    // Force carousel reinitialization
    nextTick(() => {
      if (emblaMainApi.value) {
        emblaMainApi.value.reInit()
      }
      if (emblaThumbnailApi.value) {
        emblaThumbnailApi.value.reInit()
      }
      
      // Update height and sync positions
      setTimeout(() => {
        updateMainCarouselHeight()
        syncThumbnailPosition()
        centerMobileThumbnail()
      }, 100)
    })
  }
}, { deep: true })

// Watch for selectedIndex changes to center mobile thumbnail
watch(selectedIndex, () => {
  centerMobileThumbnail()
})

onMounted(() => {
  updateMainCarouselHeight()
  // Center the initial thumbnail on mobile
  setTimeout(() => centerMobileThumbnail(), 100)
})

onBeforeUnmount(() => {
  // Clean up carousel event listeners
  if (emblaMainApi.value) {
    emblaMainApi.value.off('select', onSelect)
    emblaMainApi.value.off('reInit', onSelect)
    emblaMainApi.value.off('resize', updateMainCarouselHeight)
  }
})
</script>

<style scoped>
/* Smooth scrolling for iOS mobile thumbnail gallery */
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
}

/* Custom scrollbar for mobile thumbnails */
.scrollbar-thin::-webkit-scrollbar {
  height: 4px;
}

.scrollbar-track-gray-100::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.dark .scrollbar-track-gray-700::-webkit-scrollbar-track {
  background: #374151;
}

.scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.dark .scrollbar-thumb-gray-500::-webkit-scrollbar-thumb {
  background: #6b7280;
  border-radius: 2px;
}

/* Mobile optimizations */
@media (max-width: 767px) {
  /* Ensure carousel images are touch-friendly */
  .relative img {
    touch-action: pan-y pinch-zoom;
  }
  
  /* Make navigation arrows more visible on mobile */
  .absolute button {
    opacity: 0.9 !important;
  }
  
  /* Improve thumbnail touch targets */
  .flex-shrink-0 {
    min-width: 80px;
    min-height: 80px;
  }
}

/* Animation for swipe indicator */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
